<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-message-merging — account default level (Inmediato / Equilibrado /
// Agrupar) for agents who have not picked their own, plus an optional lock so
// agents cannot change it. Settings → Cuenta is administrator-gated, so these
// controls are admin-only by construction. Self-gated by KAPTION_MESSAGE_MERGING.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import {
  KAPTION_MERGE_DEFAULT_LEVEL,
  KAPTION_MERGE_LEVELS,
} from 'dashboard/helper/kaptionMessageMerge';
import SectionLayout from './SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const levels = Object.values(KAPTION_MERGE_LEVELS);
const defaultLevel = ref(KAPTION_MERGE_DEFAULT_LEVEL);
const locked = ref(false);

watch(
  currentAccount,
  () => {
    const settings = currentAccount.value?.settings || {};
    defaultLevel.value = levels.includes(settings.kaption_merge_default)
      ? settings.kaption_merge_default
      : KAPTION_MERGE_DEFAULT_LEVEL;
    locked.value = !!settings.kaption_merge_locked;
  },
  { deep: true, immediate: true }
);

const isVisible = computed(() =>
  isKaptionFlagEnabled('KAPTION_MESSAGE_MERGING')
);

const save = async () => {
  try {
    await updateAccount({
      kaption_merge_default: defaultLevel.value,
      kaption_merge_locked: locked.value,
    });
    useAlert(t('KAPTION_MESSAGE_MERGE.ADMIN.API.SUCCESS'));
  } catch (error) {
    useAlert(t('KAPTION_MESSAGE_MERGE.ADMIN.API.ERROR'));
  }
};

const selectLevel = async level => {
  defaultLevel.value = level;
  await save();
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <SectionLayout
    v-if="isVisible"
    :title="t('KAPTION_MESSAGE_MERGE.ADMIN.TITLE')"
    :description="t('KAPTION_MESSAGE_MERGE.ADMIN.NOTE')"
    with-border
    data-testid="kaption-merge-account-setting"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('KAPTION_MESSAGE_MERGE.ADMIN.DEFAULT_LABEL') }}
        </span>
        <div
          role="radiogroup"
          :aria-label="t('KAPTION_MESSAGE_MERGE.ADMIN.DEFAULT_LABEL')"
          class="flex max-w-md gap-1 rounded-lg bg-n-alpha-2 p-1"
        >
          <button
            v-for="level in levels"
            :key="level"
            type="button"
            role="radio"
            :aria-checked="defaultLevel === level"
            :data-testid="`account-merge-default-${level}`"
            class="h-8 flex-1 rounded-md px-2 text-sm"
            :class="
              defaultLevel === level
                ? 'bg-n-solid-active font-semibold text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="selectLevel(level)"
          >
            {{ t(`KAPTION_MESSAGE_MERGE.SETTINGS.LEVELS.${level}`) }}
          </button>
        </div>
      </div>
      <label class="flex max-w-md items-center gap-3">
        <span class="flex min-w-0 flex-1 flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('KAPTION_MESSAGE_MERGE.ADMIN.LOCK_LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('KAPTION_MESSAGE_MERGE.ADMIN.LOCK_NOTE') }}
          </span>
        </span>
        <Switch
          v-model="locked"
          data-testid="account-merge-lock"
          @change="save"
        />
      </label>
    </div>
  </SectionLayout>
</template>
